import AppLayout from '../layouts/AppLayout'
import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react'
import { AppContext } from '../contexts/AppContext'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Spinner from 'react-activity/dist/Spinner'
import 'react-activity/dist/Spinner.css'
import { format } from 'date-fns'
import { enUS } from 'date-fns/locale'
import classNames from 'classnames'
import { getTeamImage } from '../utils/team'
import { getCompetitionImage } from '../utils/competition'
import '../styles/match.css'
import DataTable from '../components/DataTable'

const formatTimestamp = (timestamp) => {
  if (!timestamp) return ''
  let str = typeof timestamp === 'string' ? timestamp : String(timestamp)
  if (!str.endsWith('Z') && !str.includes('+') && !str.includes('-', 10)) str += 'Z'
  const date = new Date(str)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })
}

const formatElapsedTime = (addedat, deletedat) => {
  if (!addedat || !deletedat) return ''

  let addedStr = typeof addedat === 'string' ? addedat : String(addedat)
  let deletedStr = typeof deletedat === 'string' ? deletedat : String(deletedat)
  if (!addedStr.endsWith('Z') && !addedStr.includes('+') && !addedStr.includes('-', 10)) {
    addedStr += 'Z'
  }
  if (!deletedStr.endsWith('Z') && !deletedStr.includes('+') && !deletedStr.includes('-', 10)) {
    deletedStr += 'Z'
  }

  const addedMs = new Date(addedStr).getTime()
  const deletedMs = new Date(deletedStr).getTime()
  if (isNaN(addedMs) || isNaN(deletedMs)) return ''
  const diffMs = deletedMs - addedMs
  if (diffMs < 0) return ''
  const totalMinutes = Math.floor(diffMs / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 0) return `${minutes} Minutes`
  if (minutes === 0) return `${hours} Hours`
  return `${hours} Hours ${minutes} Minutes`
}

const MemberMatchDetails = () => {
  const { matchId, competition } = useParams()

  const navigate = useNavigate()

  const [match, setMatch] = useState('')
  const [type, setType] = useState('Live')
  const [matchLinks, setmatchLinks] = useState([])
  const [activeSection, setActiveSection] = useState('links')
  const [linksLoading, setslinksLoading] = useState(false)
  const [liveCount, setliveCount] = useState(0)
  const [highlightCount, sethighlightCount] = useState(0)
  const [googleCount, setgoogleCount] = useState(0)
  const [totalSpectators, settotalSpectators] = useState(0)
  const [websitesLinks, setwebsitesLinks] = useState([])
  const [team1scorers, setTeam1scorers] = useState([])
  const [team2scorers, setTeam2scorers] = useState([])
  const [competitions, setCompetitions] = useState([])

  const { getmatch, getteamscorers, getmatchlinks, isLoading, getCompetitions } = useContext(AppContext)

  const fetchCompetitions = useCallback(async () => {
    const response = await getCompetitions()
    setCompetitions(response)
  }, [getCompetitions])

  const selectedCompetition = useMemo(
    () => competitions.find((comp) => comp.id === Number(competition)),
    [competitions, competition]
  )

  useEffect(() => {
    fetchCompetitions()
  }, [fetchCompetitions])

  useEffect(() => {
    if (matchId) {
      const fetchMatch = async () => {
        const response = await getmatch(matchId)
        if (response) {
          if (response[0] !== '') {
            setMatch(response[0])

            if (response[0].team1score !== '') {
              const response4 = await getteamscorers(response[0].team1, matchId)
              setTeam1scorers(response4)
            }
            if (response[0].team2score !== '') {
              const response5 = await getteamscorers(response[0].team2, matchId)
              setTeam2scorers(response5)
            }
          } else {
            navigate('/all-matches')
          }
        } else {
          navigate('/all-matches')
        }
      }

      fetchMatch()
    }
  }, [matchId, getmatch, navigate])

  const fetchMatchlinks = async (type) => {
    setslinksLoading(true)
    const response = await getmatchlinks(type, matchId)
    setmatchLinks(response.matchlinks)
    setliveCount(response.totallive)
    sethighlightCount(response.totalhighlights)
    setgoogleCount(response.totalgoogle)
    setwebsitesLinks(response.totalwebsites)
    settotalSpectators(response.totalspectators)
    setslinksLoading(false)
  }

  useEffect(() => {
    if (matchId) {
      fetchMatchlinks(type)
    }
  }, [type, matchId])

  const handleSetType = async (t) => {
    setType(t)
    setActiveSection('links')
    fetchMatchlinks(t)
  }

  const getDomain = (link, domain) => {
    if (domain) return domain
    try {
      return new URL(link).hostname
    } catch {
      return ''
    }
  }

  const websiteIconMap = useMemo(() => {
    const map = new Map()
    if (websitesLinks) {
      websitesLinks.forEach((website) => {
        if (website.link) {
          website.link.split(',').forEach((domain) => {
            map.set(domain.trim().toLowerCase(), website.icon)
          })
        }
      })
    }
    return map
  }, [websitesLinks])

  const getWebsiteIcon = (link) => {
    try {
      const hostname = new URL(link).hostname.toLowerCase().replace(/^www\./, '')
      for (const [domain, icon] of websiteIconMap) {
        if (hostname === domain || hostname.endsWith('.' + domain)) {
          return icon
        }
      }
    } catch {}
    return null
  }

  const linkTableColumns = useMemo(() => {
    const cols = []
    cols.push({
      Header: 'LINK',
      accessor: 'link',
      Cell: ({ value }) => {
        const icon = getWebsiteIcon(value)
        return (
          <div className='flex items-center gap-2'>
            {icon && (
              <img
                width='16'
                height='16'
                src={`${process.env.REACT_APP_STATIC_URL || ''}${icon}`}
              />
            )}
            <a
              href={value}
              target='_blank'
              rel='noreferrer'
              className='text-blue-600 hover:underline truncate block max-w-[400px]'
            >
              {value}
            </a>
          </div>
        )
      },
    })
    cols.push(
      { Header: 'TYPE', accessor: 'type' },
      {
        Header: 'DOMAIN',
        accessor: (row) => getDomain(row.link, row.domain),
        id: 'domain',
      },
      {
        Header: 'NOTICED TIME',
        accessor: 'addedat',
        Cell: ({ value }) => (value ? formatTimestamp(value) : '–'),
      },
      {
        Header: 'TAKE DOWN TIME',
        accessor: 'deletedat',
        Cell: ({ value }) => (value ? formatTimestamp(value) : '–'),
      },
      {
        Header: 'TOTAL TIME ELAPSED',
        id: 'elapsedtime',
        Cell: ({ row }) => {
          const { addedat, deletedat } = row.original
          const result = formatElapsedTime(addedat, deletedat)
          return result || '–'
        },
      }
    )
    return cols
  }, [type, websiteIconMap])

  return (
    <AppLayout pageName={`${selectedCompetition?.name}`}>
      {match !== '' ? (
        <>
          <section>
            <div className='py-5'>
              <div className='flex justify-center space-x-4 mb-10'>
                <div className='flex space-x-2 font-normal tracking-wider items-center text-white p-2 rounded-full bg-main pr-3'>
                  {match.complogo && (
                    <img
                      alt={match.compname}
                      className='rounded-full border-4 border-white'
                      width='30'
                      height='30'
                      src={getCompetitionImage(match.complogo)}
                    />
                  )}{' '}
                  <span>{match.compname}</span>
                </div>
                <div className='text-lg tracking-wide p-2 px-5 rounded-full bg-white border text-main-alt border-main-alt'>
                  {match.status}
                </div>
              </div>
              <div className='flex flex-row flex-wrap md:flex-nowrap justify-center space-x-0 md:space-x-24 heading'>
                <div className='flex flex-col w-6/12 md:w-auto space-y-4 md:space-y-8 text-lg md:text-3xl font-bold text-main items-center text-center order-2 md:order-1'>
                  <Link to={`/team/${match.team1name.toLowerCase().replaceAll(' ', '')}/${match.team1}`}>
                    <img
                      alt={match.team1name}
                      className='w-16 h-16 md:w-24 md:h-24'
                      src={getTeamImage(match.team1logo, match.team1name)}
                    />
                  </Link>
                  <Link to={`/team/${match.team1name.toLowerCase().replaceAll(' ', '')}/${match.team1}`}>
                    <div>{match.team1name}</div>
                  </Link>
                </div>
                <div className='flex flex-col w-full md:w-auto space-y-4 text-3xl font-bold text-black text-center justify-center order-1 md:order-2 mb-6 md:mb-0'>
                  {match.matchday !== '' && (
                    <div className='text-base tracking-wide text-neutral-400 font-light'>Matchday {match.matchday}</div>
                  )}
                  <div className=''>{format(new Date(match.date), 'EEE, do MMM yyyy', { locale: enUS })}</div>
                  <div className='flex justify-center'>
                    <div className='text-lg tracking-wide p-1 px-5 rounded-full bg-neutral-100 text-neutral-800 mt-1'>
                      {match.time}
                    </div>
                  </div>
                  {match.team1score != null && match.team2score != null && (
                    <>
                      <div className='flex justify-center items-top space-x-5'>
                        <div>
                          <div
                            className={classNames({
                              'rounded py-2 w-12 text-center text-white': match.team1score != null,
                              'bg-main-alt': match.team1score > match.team2score,
                              'bg-red-700': match.team1score < match.team2score,
                              'bg-neutral-500': match.team1score !== null && match.team1score === match.team2score,
                            })}
                          >
                            {match.team1score}
                          </div>
                        </div>
                        <div className='text-lg font-bold text-neutral-500 pt-2.5'>-</div>
                        <div>
                          <div
                            className={classNames({
                              'rounded py-2 w-12 text-center text-white': match.team2score != null,
                              'bg-main-alt': match.team2score > match.team1score,
                              'bg-red-600': match.team2score < match.team1score,
                              'bg-neutral-500': match.team1score !== null && match.team1score === match.team2score,
                            })}
                          >
                            {match.team2score}
                          </div>
                        </div>
                      </div>
                      <div className='flex justify-center items-top'>
                        <div className='flex-1'>
                          {match.team1score > 0 && (
                            <div className='space-y-1 text-neutral-500 text-xs text-right font-normal normal-font'>
                              {team1scorers.map((scorer, index) => {
                                return (
                                  <div
                                    className='whitespace-nowrap'
                                    key={index}
                                  >
                                    {scorer.playername}{' '}
                                    {scorer.origteamid == match.team2 && (
                                      <span className='text-red-500'>(Own Goal)</span>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                        <div className='text-lg font-bold text-neutral-500 w-11'></div>
                        <div className='flex-1'>
                          {match.team2score > 0 && (
                            <div className='space-y-1 text-neutral-500 text-xs text-left font-normal normal-font'>
                              {team2scorers.map((scorer, index) => {
                                return (
                                  <div
                                    className='whitespace-nowrap'
                                    key={index}
                                  >
                                    {scorer.playername}{' '}
                                    {scorer.origteamid == match.team1 && (
                                      <span className='text-red-500'>(Own Goal)</span>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className='flex flex-col w-6/12 md:w-auto space-y-4 md:space-y-8 text-lg md:text-3xl font-bold text-main items-center text-center order-3 md:order-3'>
                  <Link to={`/team/${match.team2name.toLowerCase().replaceAll(' ', '')}/${match.team2}`}>
                    <img
                      alt={match.team2name}
                      className='w-16 h-16 md:w-24 md:h-24'
                      src={getTeamImage(match.team2logo, match.team2name)}
                    />
                  </Link>
                  <Link to={`/team/${match.team2name.toLowerCase().replaceAll(' ', '')}/${match.team2}`}>
                    <div>{match.team2name}</div>
                  </Link>
                </div>
              </div>
            </div>
          </section>
          <section>
            <div className='flex flex-col lg:flex-col justify-between items-center space-x-0 lg:space-x-3'>
              <div className='text-center'>
                <h2 className='text-2xl font-bold tracking-normal text-main mb-2'>Match Links</h2>
                <div className='flex flex-row items-center space-x-3 mb-2'>
                  <div>
                    <span className='bg-blue-100 text-blue-800 text-sm font-medium px-3 py-2 rounded'>
                      Total links:{' '}
                      {liveCount + highlightCount + googleCount > 0
                        ? (liveCount + highlightCount + googleCount).toLocaleString()
                        : '0'}
                    </span>
                  </div>
                  <div>
                    <span className='bg-green-100 text-green-800 text-sm font-medium px-3 py-2 rounded'>
                      Number of Spectators:
                      {totalSpectators > 0
                        ? ` ${Intl.NumberFormat('en-US').format(parseInt(totalSpectators)).replace(/,/g, ' ')}`
                        : 0}
                    </span>
                  </div>
                </div>
              </div>
              {websitesLinks && (
                <div className='flex flex-wrap flex-row items-center justify-center mb-4'>
                  {websitesLinks.map((website, index) => {
                    return (
                      <React.Fragment key={index}>
                        <div className='flex space-x-2 border rounded p-1 pr-2 m-2 items-center'>
                          <div>
                            <img
                              width='40'
                              height='40'
                              src={`${process.env.REACT_APP_STATIC_URL || ''}${website.icon}`}
                            />
                          </div>
                          <div>
                            <h6 className='text-neutral-500 text-xs'>{website.name}</h6>
                            <h5 className='text-main-alt leading-none mt-0.5 font-bold'>
                              {website.name == 'Google' || website.name == 'Bing' ? googleCount : website.total}
                            </h5>
                          </div>
                        </div>
                      </React.Fragment>
                    )
                  })}
                </div>
              )}
            </div>
            <div className='pills w-full rounded-lg p-0 text-black font-semibold flex flex-row'>
              <button
                className={`${
                  activeSection === 'links' && type === 'Live'
                    ? 'bg-white border border-b-white'
                    : 'hover:bg-neutral-50 border-b text-neutral-500'
                } rounded-t-lg w-1/3 p-2 md:p-4 flex flex-col md:flex-row justify-center items-center space-x-0 md:space-x-2`}
                onClick={() => {
                  setActiveSection('links')
                  handleSetType('Live')
                }}
              >
                <span>Live</span>
                {liveCount > 0 && (
                  <span className='bg-green-100 text-green-800 text-sm font-medium px-2.5 py-0.5 rounded'>
                    {liveCount}
                  </span>
                )}
              </button>
              <button
                className={`${
                  activeSection === 'links' && type === 'Highlight'
                    ? 'bg-white border border-b-white'
                    : 'hover:bg-neutral-50 border-b text-neutral-500'
                } rounded-t-lg w-1/3 p-2 md:p-4 flex flex-col md:flex-row justify-center items-center space-x-0 md:space-x-2`}
                onClick={() => {
                  setActiveSection('links')
                  handleSetType('Highlight')
                }}
              >
                <span>Highlights</span>
                {highlightCount > 0 && (
                  <span className='bg-green-100 text-green-800 text-sm font-medium px-2.5 py-0.5 rounded'>
                    {highlightCount}
                  </span>
                )}
              </button>
              <button
                className={`${
                  activeSection === 'links' && type === 'Google'
                    ? 'bg-white border border-b-white'
                    : 'hover:bg-neutral-50 border-b text-neutral-500'
                } rounded-t-lg w-1/3 p-2 md:p-4 flex flex-col md:flex-row justify-center items-center space-x-0 md:space-x-2`}
                onClick={() => {
                  setActiveSection('links')
                  handleSetType('Google')
                }}
              >
                <span>Google & Bing</span>
                {googleCount > 0 && (
                  <span className='bg-green-100 text-green-800 text-sm font-medium px-2.5 py-0.5 rounded'>
                    {googleCount}
                  </span>
                )}
              </button>
            </div>
            {activeSection === 'links' && (
              <section className='space-y-6 !rounded-t-none !border-t-0'>
                <div>
                  {linksLoading ? (
                    <div className='flex justify-center w-full py-12'>
                      <Spinner
                        color='#CCCCCC'
                        size={24}
                      />
                    </div>
                  ) : (
                    <DataTable
                      data={matchLinks}
                      columns={linkTableColumns}
                      defaultPageSize={50}
                      pageSizeOptions={[10, 25, 50, 100]}
                    />
                  )}
                </div>
              </section>
            )}
          </section>
        </>
      ) : (
        <div className='flex justify-center w-full py-12'>
          <Spinner
            color='#CCCCCC'
            size={24}
          />
        </div>
      )}
    </AppLayout>
  )
}

export default MemberMatchDetails
